import React, { useEffect, useState } from "react";
import { Shield, Plus, Trash2, Save, RotateCcw, Loader2, Lock, ChevronsUpDown, X } from "lucide-react";
import { api } from "../api";

/**
 * Roles & Hierarchy — workspace-scoped role manager.
 * Each workspace owns its own hierarchy (level 0 = boss, higher = further from top).
 * System roles (boss / self) cannot be deleted; only their label can be edited.
 */
export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/roles");
      setRoles(data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load roles");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const updateField = (id, patch) => setRoles(rs => rs.map(r => r.id === id ? { ...r, ...patch } : r));

  const saveRole = async (r) => {
    setSaving(s => ({ ...s, [r.id]: true }));
    try {
      const { data } = await api.put(`/roles/${r.id}`, {
        key: r.key,
        label: r.label,
        level: r.level,
        can_manage_users: r.can_manage_users,
        can_manage_projects: r.can_manage_projects,
      });
      setRoles(rs => rs.map(x => x.id === data.id ? data : x));
    } catch (e) {
      alert(e?.response?.data?.detail || "Save failed");
    }
    setSaving(s => ({ ...s, [r.id]: false }));
  };

  const deleteRole = async (r) => {
    if (r.system) return;
    if (!window.confirm(`Delete role "${r.label}"? Users on this role will be moved to "member".`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      setRoles(rs => rs.filter(x => x.id !== r.id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm("Reset the hierarchy to the default template for your plan? Custom roles will be removed.")) return;
    try {
      const { data } = await api.post("/roles/reset");
      setRoles(data.roles || []);
    } catch (e) {
      alert(e?.response?.data?.detail || "Reset failed");
    }
  };

  const sorted = [...roles].sort((a, b) => (a.level || 0) - (b.level || 0));

  return (
    <div className="h-full w-full flex flex-col bg-slate-50 text-slate-800 hexa-force-light" data-testid="app-roles">
      <div className="px-4 py-3 border-b border-slate-200 bg-white flex items-center gap-3">
        <Shield className="w-5 h-5 text-indigo-500" />
        <div className="text-sm font-bold">Roles & Hierarchy</div>
        <div className="text-[11px] text-slate-500">Workspace-scoped · defines who can see & manage what</div>
        <div className="flex-1" />
        <button onClick={resetToDefault} className="text-xs font-semibold px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 text-slate-700 flex items-center gap-1" data-testid="roles-reset-btn">
          <RotateCcw size={12} /> Reset to defaults
        </button>
        <button onClick={() => setShowAdd(true)} className="text-xs font-semibold px-3 py-1.5 rounded bg-gradient-to-r from-indigo-600 to-purple-600 text-white flex items-center gap-1 hover:opacity-90" data-testid="roles-new-btn">
          <Plus size={12} /> New role
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {error && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">{error}</div>}
        {loading ? (
          <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>
        ) : sorted.length === 0 ? (
          <div className="text-center text-slate-500 py-12 text-sm">No roles yet — click &ldquo;Reset to defaults&rdquo; to seed your hierarchy.</div>
        ) : (
          <div className="max-w-4xl mx-auto space-y-3">
            <div className="grid grid-cols-12 gap-3 px-3 text-[10px] uppercase tracking-widest font-bold text-slate-500">
              <div className="col-span-1">Level</div>
              <div className="col-span-3">Key</div>
              <div className="col-span-3">Label</div>
              <div className="col-span-2 text-center">Manage users</div>
              <div className="col-span-2 text-center">Manage projects</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {sorted.map((r) => (
              <div key={r.id} className="bg-white border border-slate-200 rounded-lg shadow-sm hover:shadow transition" data-testid={`role-row-${r.key}`}>
                <div className="grid grid-cols-12 gap-3 px-3 py-3 items-center">
                  <div className="col-span-1">
                    <input
                      type="number"
                      min={0}
                      max={20}
                      value={r.level ?? 0}
                      disabled={r.system}
                      onChange={(e) => updateField(r.id, { level: Number(e.target.value) })}
                      className="w-16 text-sm px-2 py-1.5 rounded border border-slate-200 focus:border-indigo-400 outline-none font-bold text-center disabled:bg-slate-100"
                    />
                  </div>
                  <div className="col-span-3 flex items-center gap-1.5">
                    <span className="text-xs font-mono text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{r.key}</span>
                    {r.system && <Lock size={10} className="text-slate-400" title="System role" />}
                  </div>
                  <div className="col-span-3">
                    <input
                      value={r.label || ""}
                      onChange={(e) => updateField(r.id, { label: e.target.value })}
                      className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:border-indigo-400 outline-none"
                    />
                  </div>
                  <div className="col-span-2 flex justify-center">
                    <input
                      type="checkbox"
                      checked={!!r.can_manage_users}
                      disabled={r.system}
                      onChange={(e) => updateField(r.id, { can_manage_users: e.target.checked })}
                      className="w-4 h-4"
                    />
                  </div>
                  <div className="col-span-2 flex justify-center">
                    <input
                      type="checkbox"
                      checked={!!r.can_manage_projects}
                      disabled={r.system}
                      onChange={(e) => updateField(r.id, { can_manage_projects: e.target.checked })}
                      className="w-4 h-4"
                    />
                  </div>
                  <div className="col-span-1 flex justify-end gap-1">
                    <button onClick={() => saveRole(r)} disabled={!!saving[r.id]} className="p-1.5 rounded hover:bg-indigo-50 text-indigo-600 disabled:opacity-60" title="Save">
                      {saving[r.id] ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                    </button>
                    {!r.system && (
                      <button onClick={() => deleteRole(r)} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Delete">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div className="mt-4 text-[11px] text-slate-500 flex items-start gap-2">
              <ChevronsUpDown size={12} className="mt-0.5" />
              <div>Lower <b>Level</b> = higher authority. Level 0 is the top of the hierarchy (Boss / Admin). Users on roles with <b>Manage users</b> can invite &amp; assign roles. <b>System</b> roles are locked (only the label can be renamed).</div>
            </div>
          </div>
        )}
      </div>

      {showAdd && <NewRoleModal existing={sorted} onClose={() => setShowAdd(false)} onCreated={(r) => { setRoles(rs => [...rs, r]); setShowAdd(false); }} />}
    </div>
  );
}

function NewRoleModal({ existing, onClose, onCreated }) {
  const [form, setForm] = useState({ key: "", label: "", level: (existing[existing.length - 1]?.level || 0) + 1, can_manage_users: false, can_manage_projects: true });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.label.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/roles", {
        key: form.key || form.label.toLowerCase().replace(/[^a-z0-9]+/g, "_"),
        label: form.label,
        level: Number(form.level),
        can_manage_users: form.can_manage_users,
        can_manage_projects: form.can_manage_projects,
      });
      onCreated(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Create failed");
    }
    setBusy(false);
  };
  return (
    <div className="absolute inset-0 z-40 bg-black/40 flex items-center justify-center p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white rounded-lg shadow-2xl overflow-hidden" data-testid="roles-new-modal">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-semibold">New role</div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded text-slate-500"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Display label">
            <input autoFocus value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value, key: form.key || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_") })} placeholder="e.g. QA Engineer" className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:border-indigo-400 outline-none" data-testid="new-role-label" />
          </Field>
          <Field label="Key (used in code)">
            <input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })} placeholder="qa_engineer" className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:border-indigo-400 outline-none font-mono" data-testid="new-role-key" />
          </Field>
          <Field label="Hierarchy level (0 = top)">
            <input type="number" min={0} max={20} value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:border-indigo-400 outline-none" />
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.can_manage_users} onChange={(e) => setForm({ ...form, can_manage_users: e.target.checked })} /> Can manage users
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.can_manage_projects} onChange={(e) => setForm({ ...form, can_manage_projects: e.target.checked })} /> Can manage projects
          </label>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded hover:bg-slate-200 text-slate-700">Cancel</button>
          <button onClick={submit} disabled={busy || !form.label.trim()} className="text-sm px-3 py-1.5 rounded bg-gradient-to-r from-indigo-600 to-purple-600 text-white disabled:opacity-60 flex items-center gap-1" data-testid="new-role-submit">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Create role
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
